import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Back, ForbiddenIcon, ForwardIcon } from '../../../../components/Icons';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css'; // core Swiper

import './HourSlider.css';
import { Swiper as SwiperRef } from 'swiper/types';
import { Config } from '../../../../helpers/config';

type HourSliderProps = {
  afterChange?: (index: number) => void;
  initialIndex?: [number];
  isDisabled?: boolean;
  spans: TimeSpan[];
};

export type TimeSpan = {
  isEnable: boolean;
  time: string;
  price?: string;
};

export const HourSlider: React.FC<HourSliderProps> = ({
  afterChange,
  spans,
  initialIndex,
  isDisabled,
}) => {
  const [swiperRef, setSwiperRef] = useState<SwiperRef | null>(null);
  const isChangedProgrammatically = useRef<boolean>(false);
  const [prevAvailableIndex, setPrevAvailableIndex] = useState<number | null>(
    null
  );
  const [nextAvailableIndex, setNextAvailableIndex] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (
      initialIndex !== undefined &&
      initialIndex[0] !== swiperRef?.activeIndex
    ) {
      if (!(initialIndex[0] === 0 && !swiperRef?.activeIndex)) {
        isChangedProgrammatically.current = true;
        swiperRef?.slideTo(initialIndex[0]);
      }
    }
  }, [initialIndex, swiperRef]);

  const afterIndexChange = useCallback(
    (swiper: SwiperRef) => {
      const i = swiper.realIndex;
      const prevIndex = swiper.previousIndex;
      if (spans[i].isEnable) {
        const nextAvailable = checkForward(i, spans);
        const prevAvailable = checkBackward(i, spans);
        setNextAvailableIndex(nextAvailable);
        setPrevAvailableIndex(prevAvailable);
        if (!isChangedProgrammatically.current) {
          afterChange?.(i);
        } else {
          isChangedProgrammatically.current = false;
        }
      } else {
        const optimalIndex = searchOptimalIndex(prevIndex, i, spans);
        swiperRef?.slideTo(optimalIndex);
      }
    },
    [afterChange, spans, swiperRef]
  );

  const onPrevClick = useCallback(() => {
    swiperRef?.slideTo(prevAvailableIndex!);
  }, [prevAvailableIndex, swiperRef]);

  const onNextClick = useCallback(() => {
    swiperRef?.slideTo(nextAvailableIndex!);
  }, [nextAvailableIndex, swiperRef]);

  return (
    <div className="hour-slider--slider-container">
      <Swiper
        slidesPerView={'auto'}
        centeredSlides={true}
        onSlideChange={afterIndexChange}
        onSwiper={setSwiperRef}
        grabCursor
        slideToClickedSlide
        noSwiping={isDisabled}
        noSwipingClass="swiper-slide"
      >
        {spans.map((span) => {
          return (
            <SwiperSlide key={span.time}>
              {({ isActive }) => (
                <div
                  className={`hour-slider--carousel-hour-container ${
                    isActive ? 'hour-slider--active' : ''
                  } ${!span.isEnable ? 'hour-slider--disabled' : ''}`}
                >
                  <div className="hour-slider--carousel-hour">
                    <h3 className="no-highlight hour-slider--card-title">
                      {span.time}
                    </h3>
                    {span.price && span.isEnable && !isActive && (
                      <div className="hour-slider--price">{span.price}€</div>
                    )}
                    {!span.isEnable && (
                      <div className="hour-slider--icon">
                        <ForbiddenIcon width={10} height={10} />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </SwiperSlide>
          );
        })}
      </Swiper>
      {prevAvailableIndex !== null && (
        <div
          onClick={onPrevClick}
          className="clickable hour-slider--reminder hour-slider--reminder-left"
        >
          <Back width={10} height={10} color={Config.primaryBlue} />
        </div>
      )}
      {nextAvailableIndex !== null && (
        <div
          onClick={onNextClick}
          className="clickable hour-slider--reminder hour-slider--reminder-right"
        >
          <ForwardIcon width={10} height={10} color={Config.primaryBlue} />
        </div>
      )}
    </div>
  );
};

function checkForward(currentIndex: number, spans: TimeSpan[]): null | number {
  let nextEnbaled = currentIndex + 1;
  while (nextEnbaled < spans.length && !spans[nextEnbaled].isEnable) {
    nextEnbaled++;
  }
  if (nextEnbaled === spans.length) {
    nextEnbaled--;
  }

  if (!spans[nextEnbaled].isEnable) return null;

  return nextEnbaled - 1 > currentIndex ? nextEnbaled : null;
}

function checkBackward(currentIndex: number, spans: TimeSpan[]) {
  let nextEnbaled = currentIndex - 1;
  while (nextEnbaled > 0 && !spans[nextEnbaled].isEnable) {
    nextEnbaled--;
  }

  if (nextEnbaled < 0) {
    nextEnbaled++;
  }

  if (!spans[nextEnbaled].isEnable) return null;

  return currentIndex <= nextEnbaled + 1 ? null : nextEnbaled;
}

function searchOptimalIndex(
  currentIndex: number,
  nextIndex: number,
  spans: TimeSpan[]
) {
  let optimalIndex = nextIndex;
  if (currentIndex < nextIndex) {
    optimalIndex = searchForward(optimalIndex, spans.length, spans);
    if (!spans[optimalIndex].isEnable) {
      optimalIndex = searchBackward(nextIndex, currentIndex, spans);
    }
  } else {
    optimalIndex = searchBackward(optimalIndex, 0, spans);
    if (!spans[optimalIndex].isEnable) {
      optimalIndex = searchForward(nextIndex, currentIndex, spans);
    }
  }

  return optimalIndex;
}
function searchForward(
  optimalIndex: number,
  lastIndex: number,
  spans: TimeSpan[]
) {
  while (optimalIndex < lastIndex && !spans[optimalIndex].isEnable) {
    optimalIndex++;
  }
  return optimalIndex === spans.length ? optimalIndex - 1 : optimalIndex;
}

function searchBackward(
  optimalIndex: number,
  lastIndex: number,
  spans: TimeSpan[]
) {
  while (optimalIndex > lastIndex && !spans[optimalIndex].isEnable) {
    optimalIndex--;
  }
  return optimalIndex;
}
